import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { PlannerContext } from "./PlannerContext";
import useDayPlanAPIClient from "../../api/dayPlans/hooks/useDayPlanAPIClient";
import useTemplatesAPIClient from "../../api/templates/hooks/useTemplatesAPIClient";
import { DEFAULT_ACTIVITY_COLOR } from "../../constants/pickerColors";

// Helper to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper to add days to a date string (YYYY-MM-DD format)
const addDaysToDate = (dateString, days) => {
  const date = new Date(dateString + "T00:00:00");
  date.setDate(date.getDate() + days);
  // Use local date components to avoid timezone issues with toISOString()
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper to get date range for preloading (today + 9 days = 10 days)
const getInitialDateRange = () => {
  const today = getTodayDate();
  const endDate = addDaysToDate(today, 9);
  return { startDate: today, endDate };
};

// Template definitions - only "Start Fresh" is built-in
// User templates are saved to their account and loaded from savedTemplates
const TEMPLATES = {
  empty: {
    id: "empty",
    name: "Start Fresh",
    description: "Start with a blank day and add blocks as you go",
    shortDescription: "",
    dayStartTime: "08:00",
    dayEndTime: "22:00",
    blocks: [],
    emoji: "1f305", // 🌅
    color: "#808080",
  },
};

// Utility functions for time manipulation
const timeToMinutes = (time) => {
  if (!time || typeof time !== "string") {
    return 0;
  }
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

const minutesToTime = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const snapToInterval = (time, interval) => {
  const minutes = timeToMinutes(time);
  const snapped = Math.round(minutes / interval) * interval;
  return minutesToTime(snapped);
};

const PlannerContextProvider = ({ children, navigationState }) => {
  // Get user tasks from Redux
  const taskSet = useSelector((state) => state.tasks.taskSet);
  // Get user activities from Redux
  const reduxActivities = useSelector((state) => state.activities.activities);

  // API client for persistence
  const { getDayPlan, getDayPlansInRange, saveDayPlan, deleteDayPlan } =
    useDayPlanAPIClient();
  const {
    getTemplates,
    createTemplate,
    updateTemplate: updateTemplateAPI,
    deleteTemplate: deleteTemplateAPI,
  } = useTemplatesAPIClient();

  // Transform Redux tasks to planner format
  const tasks = useMemo(() => {
    return Object.values(taskSet)
      .filter((task) => !task.completed) // Only show incomplete tasks
      .map((task) => ({
        id: task.taskId,
        title: task.name,
        completed: task.completed || false,
        attributes: task.attributes || {},
        emoji: task.emoji,
        labels: task.labels,
        targeted: task.targeted,
      }));
  }, [taskSet]);

  // Transform Redux activities to planner format
  const activities = useMemo(() => {
    return Object.values(reduxActivities).map((activity) => ({
      id: activity.activityId,
      name: activity.name,
      color: activity.color || DEFAULT_ACTIVITY_COLOR,
      emoji: activity.emoji,
      description: activity.description,
      duration: activity.duration,
    }));
  }, [reduxActivities]);

  // UI state
  const [selectedTool, setSelectedTool] = useState("templateSelect");
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [activeTime, setActiveTime] = useState(null);
  const [toolPanelOpen, setToolPanelOpen] = useState(false);

  // Current date for the plan (supports future multi-day extension)
  // Initialize from navigation state if returning from another page
  const [currentDate, setCurrentDate] = useState(
    () => navigationState?.currentDate || getTodayDate(),
  );

  // Loading and persistence state
  const [isLoading, setIsLoading] = useState(true); // True during initial load
  const [isNavigating, setIsNavigating] = useState(false); // True when loading a day not in cache
  const [isSaving, setIsSaving] = useState(false);
  const [hasLoadedPlan, setHasLoadedPlan] = useState(false);

  // Day configuration
  const [dayStartTime, setDayStartTime] = useState("08:00");
  const [dayEndTime, setDayEndTime] = useState("22:00");
  const [timeInterval, setTimeInterval] = useState(30);

  // Blocks state
  const [blocks, setBlocks] = useState({});
  const [nextBlockId, setNextBlockId] = useState(1);

  // Template state
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState({});

  // Dev/testing state - mock time (only used in development)
  const [mockTime, setMockTime] = useState(null); // Format: "HH:MM" or null for real time

  // Preloaded plans cache - stores fetched plans by date
  const preloadedPlansRef = useRef({});
  const hasPreloadedWeekRef = useRef(false);

  // Refs for debounced save
  const saveTimeoutRef = useRef(null);
  const pendingSaveRef = useRef(null);
  const lastLoadedDateRef = useRef(null);
  const saveDayPlanRef = useRef(saveDayPlan);
  const currentDateRef = useRef(currentDate);

  // Ref for debounced date loading
  const dateLoadTimeoutRef = useRef(null);
  const pendingDateRef = useRef(null);

  // Keep refs up to date
  useEffect(() => {
    saveDayPlanRef.current = saveDayPlan;
  }, [saveDayPlan]);

  useEffect(() => {
    currentDateRef.current = currentDate;
  }, [currentDate]);

  // Load templates from API on mount
  useEffect(() => {
    const loadTemplatesFromAPI = async () => {
      try {
        const templates = await getTemplates();
        if (templates && Array.isArray(templates)) {
          const templatesMap = {};
          templates.forEach((template) => {
            templatesMap[template.templateId] = {
              ...template,
              id: template.templateId,
              dayStartTime: template.dayStartTime || "08:00",
              dayEndTime: template.dayEndTime || "22:00",
            };
          });
          setSavedTemplates(templatesMap);
        }
      } catch (error) {
        console.error("Error loading templates:", error);
      }
    };

    loadTemplatesFromAPI();
  }, [getTemplates]);

  // Load day plan on mount or when date changes
  // Helper function to apply a plan to state
  const applyPlanToState = useCallback((plan, date) => {
    if (plan && !plan.message) {
      // Plan exists, load it
      setDayStartTime(plan.dayStartTime || "08:00");
      setDayEndTime(plan.dayEndTime || "22:00");
      setTimeInterval(plan.timeInterval || 30);
      setBlocks(plan.blocks || {});
      setCurrentTemplate(plan.templateId || null);

      // Calculate next block ID from existing blocks
      const maxId = Object.keys(plan.blocks || {}).reduce(
        (max, id) => Math.max(max, parseInt(id, 10) || 0),
        0,
      );
      setNextBlockId(maxId + 1);

      // Skip template selection since we have a saved plan
      setSelectedTool("dayConfig");
      setHasLoadedPlan(true);
    } else {
      // No plan exists for this date - reset to fresh state and show template selection
      setDayStartTime("08:00");
      setDayEndTime("22:00");
      setTimeInterval(30);
      setBlocks({});
      setNextBlockId(1);
      setCurrentTemplate(null);
      setSelectedBlockId(null);
      setHasLoadedPlan(false);
      setSelectedTool("templateSelect");
    }
    lastLoadedDateRef.current = date;
  }, []);

  // Preload 10 days worth of plans on mount
  useEffect(() => {
    const preloadInitialDays = async () => {
      if (hasPreloadedWeekRef.current) return;

      try {
        const { startDate, endDate } = getInitialDateRange();
        const plans = await getDayPlansInRange(startDate, endDate);

        // First, mark all days in range as cached (null = no plan exists)
        // This prevents API calls for empty days within the preloaded range
        let currentDateStr = startDate;
        while (currentDateStr <= endDate) {
          preloadedPlansRef.current[currentDateStr] = null;
          currentDateStr = addDaysToDate(currentDateStr, 1);
        }

        // Then overwrite with actual plans that exist
        if (plans && Array.isArray(plans)) {
          plans.forEach((plan) => {
            if (plan.date) {
              preloadedPlansRef.current[plan.date] = plan;
            }
          });
        }
        hasPreloadedWeekRef.current = true;

        // Check if current date is in the cached range
        const targetDate = currentDateRef.current;
        const cachedPlan = preloadedPlansRef.current[targetDate];

        // If current date is not in cache (undefined), fetch it separately
        if (cachedPlan === undefined) {
          try {
            const plan = await getDayPlan(targetDate);
            preloadedPlansRef.current[targetDate] =
              plan && !plan.message ? plan : null;
            applyPlanToState(plan, targetDate);
          } catch (err) {
            console.error("Error loading day plan for date:", targetDate, err);
            applyPlanToState(null, targetDate);
          }
        } else if (cachedPlan) {
          applyPlanToState(cachedPlan, targetDate);
        } else {
          // No plan for the date (null in cache)
          applyPlanToState(null, targetDate);
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error preloading day plans:", error);
        // Fall back to loading just today
        try {
          const plan = await getDayPlan(currentDateRef.current);
          applyPlanToState(plan, currentDateRef.current);
        } catch (err) {
          console.error("Error loading day plan:", err);
          applyPlanToState(null, currentDateRef.current);
        }
        setIsLoading(false);
      }
    };

    preloadInitialDays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDayPlansInRange, getDayPlan, applyPlanToState]);

  // Restore navigation state when returning from activities page
  useEffect(() => {
    if (!isLoading && navigationState) {
      if (navigationState.selectedBlockId) {
        setSelectedBlockId(navigationState.selectedBlockId);
      }
      if (navigationState.selectedTool) {
        setSelectedTool(navigationState.selectedTool);
      }
    }
  }, [isLoading, navigationState]);

  // Handle date changes with debouncing for smooth navigation
  useEffect(() => {
    // Skip initial load (handled by preload effect)
    if (!hasPreloadedWeekRef.current) return;

    // Prevent re-loading if we've already loaded for this date
    if (lastLoadedDateRef.current === currentDate) return;

    // Clear any pending date load
    if (dateLoadTimeoutRef.current) {
      clearTimeout(dateLoadTimeoutRef.current);
    }

    // Check if we have this date in cache - if so, apply immediately without loading state
    const cachedPlan = preloadedPlansRef.current[currentDate];
    if (cachedPlan !== undefined) {
      // Cached plan exists (could be a plan object or null for empty days)
      applyPlanToState(cachedPlan, currentDate);
      return;
    }

    // Not in cache - debounce the load to avoid hammering the API when skimming through dates
    pendingDateRef.current = currentDate;
    setIsNavigating(true);

    dateLoadTimeoutRef.current = setTimeout(async () => {
      // Only load if this is still the pending date
      if (pendingDateRef.current !== currentDate) return;

      try {
        const plan = await getDayPlan(currentDate);
        // Cache the result
        preloadedPlansRef.current[currentDate] =
          plan && !plan.message ? plan : null;
        applyPlanToState(plan, currentDate);
      } catch (error) {
        console.error("Error loading day plan:", error);
        applyPlanToState(null, currentDate);
      } finally {
        setIsNavigating(false);
      }
    }, 150); // 150ms debounce - quick enough to feel responsive, slow enough to skip when skimming

    // Cleanup timeout on unmount or date change
    return () => {
      if (dateLoadTimeoutRef.current) {
        clearTimeout(dateLoadTimeoutRef.current);
      }
    };
  }, [currentDate, getDayPlan, applyPlanToState]);

  // Debounced save function - uses refs to avoid dependency issues
  const debouncedSave = useCallback(() => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Schedule a new save
    saveTimeoutRef.current = setTimeout(async () => {
      const planData = pendingSaveRef.current;
      if (!planData) return;

      // Update the cache with the new plan data
      const dateToSave = currentDateRef.current;
      preloadedPlansRef.current[dateToSave] = {
        ...planData,
        date: dateToSave,
      };

      setIsSaving(true);
      try {
        await saveDayPlanRef.current(dateToSave, planData);
      } catch (error) {
        console.error("Error saving day plan:", error);
      } finally {
        setIsSaving(false);
      }
    }, 500); // 500ms debounce
  }, []); // No dependencies - uses refs

  // Trigger save when plan data changes (after initial load)
  useEffect(() => {
    // Don't save during initial load or if we haven't loaded yet
    if (isLoading || !hasLoadedPlan) return;

    // Update pending save data
    pendingSaveRef.current = {
      dayStartTime,
      dayEndTime,
      timeInterval,
      blocks,
      templateId: currentTemplate,
    };

    // Trigger debounced save
    debouncedSave();

    // Cleanup on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    blocks,
    dayStartTime,
    dayEndTime,
    timeInterval,
    currentTemplate,
    hasLoadedPlan,
    isLoading,
    debouncedSave,
  ]);

  // Get sorted blocks array
  const sortedBlocks = useMemo(() => {
    return Object.values(blocks).sort(
      (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
    );
  }, [blocks]);

  // Get current time - returns mock time if set (dev mode), otherwise real time
  const getCurrentTime = useCallback(() => {
    if (mockTime) {
      return mockTime;
    }
    const now = new Date();
    return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  }, [mockTime]);

  // Load a template
  const loadTemplate = useCallback(
    (templateId) => {
      const template = savedTemplates[templateId] || TEMPLATES[templateId];
      if (!template) return;

      setDayStartTime(template.dayStartTime || "08:00");
      setDayEndTime(template.dayEndTime || "22:00");
      setCurrentTemplate(templateId);

      // Convert template blocks to state format
      const newBlocks = {};
      let maxId = 0;
      const templateBlocks = template.blocks || [];
      templateBlocks.forEach((block, index) => {
        const id = block.id || index + 1;
        maxId = Math.max(maxId, id);
        newBlocks[id] = { ...block, id };
      });

      setBlocks(newBlocks);
      setNextBlockId(maxId + 1);
      setSelectedTool("dayConfig");
      setSelectedBlockId(null);

      // Mark as loaded so persistence starts
      setHasLoadedPlan(true);
    },
    [savedTemplates],
  );

  // Add a new block
  const addBlock = useCallback(
    (blockData) => {
      const id = nextBlockId;
      const { startTime, endTime, type, ...rest } = blockData;

      // Snap times to interval
      const snappedStart = snapToInterval(startTime, timeInterval);
      const snappedEnd = snapToInterval(endTime, timeInterval);

      const newBlock = {
        id,
        type,
        startTime: snappedStart,
        endTime: snappedEnd,
        name: rest.name || `New ${type} block`,
        ...rest,
      };

      // Adjust adjacent blocks to make room
      const updatedBlocks = { ...blocks };

      Object.values(updatedBlocks).forEach((block) => {
        const blockStart = timeToMinutes(block.startTime);
        const blockEnd = timeToMinutes(block.endTime);
        const newStart = timeToMinutes(snappedStart);
        const newEnd = timeToMinutes(snappedEnd);

        // If new block overlaps with existing block, adjust existing block
        if (newStart < blockEnd && newEnd > blockStart) {
          if (newStart <= blockStart && newEnd >= blockEnd) {
            // New block completely covers existing - remove it
            delete updatedBlocks[block.id];
          } else if (newStart > blockStart && newEnd < blockEnd) {
            // New block is in the middle - shrink existing block's end
            updatedBlocks[block.id] = {
              ...block,
              endTime: snappedStart,
            };
          } else if (newStart <= blockStart) {
            // New block overlaps start - adjust start time
            updatedBlocks[block.id] = {
              ...block,
              startTime: snappedEnd,
            };
          } else {
            // New block overlaps end - adjust end time
            updatedBlocks[block.id] = {
              ...block,
              endTime: snappedStart,
            };
          }
        }
      });

      updatedBlocks[id] = newBlock;
      setBlocks(updatedBlocks);
      setNextBlockId(id + 1);
      setActiveTime(null);
      setSelectedBlockId(id);
      setSelectedTool("editBlock");

      return id;
    },
    [blocks, nextBlockId, timeInterval],
  );

  // Update an existing block
  const updateBlock = useCallback(
    (blockId, updates) => {
      setBlocks((prev) => {
        if (!prev[blockId]) return prev;

        const updatedBlock = { ...prev[blockId], ...updates };

        // Snap times if they were updated
        if (updates.startTime) {
          updatedBlock.startTime = snapToInterval(
            updates.startTime,
            timeInterval,
          );
        }
        if (updates.endTime) {
          updatedBlock.endTime = snapToInterval(updates.endTime, timeInterval);
        }

        const updatedBlocks = { ...prev, [blockId]: updatedBlock };
        const updatedStart = timeToMinutes(updatedBlock.startTime);
        const updatedEnd = timeToMinutes(updatedBlock.endTime);

        // Adjust adjacent blocks if time changes cause overlaps
        Object.values(prev).forEach((block) => {
          if (block.id === blockId) return; // Skip the block being updated

          const blockStart = timeToMinutes(block.startTime);
          const blockEnd = timeToMinutes(block.endTime);

          // Check for overlap
          if (updatedStart < blockEnd && updatedEnd > blockStart) {
            if (updatedStart <= blockStart && updatedEnd >= blockEnd) {
              // Updated block completely covers existing - remove it
              delete updatedBlocks[block.id];
            } else if (updatedStart > blockStart && updatedEnd < blockEnd) {
              // Updated block is in the middle - shrink existing block's end
              updatedBlocks[block.id] = {
                ...block,
                endTime: updatedBlock.startTime,
              };
            } else if (updatedStart <= blockStart) {
              // Updated block overlaps start - adjust start time
              updatedBlocks[block.id] = {
                ...block,
                startTime: updatedBlock.endTime,
              };
            } else {
              // Updated block overlaps end - adjust end time
              updatedBlocks[block.id] = {
                ...block,
                endTime: updatedBlock.startTime,
              };
            }
          }
        });

        return updatedBlocks;
      });
    },
    [timeInterval],
  );

  // Delete a block, leaving the time free
  const deleteBlock = useCallback(
    (blockId) => {
      setBlocks((prev) => {
        const blockToDelete = prev[blockId];
        if (!blockToDelete) return prev;

        const newBlocks = { ...prev };
        delete newBlocks[blockId];

        return newBlocks;
      });

      if (selectedBlockId === blockId) {
        setSelectedBlockId(null);
        setSelectedTool("dayConfig");
      }
    },
    [selectedBlockId],
  );

  // Save current day as a template
  const saveAsTemplate = useCallback(
    async (
      templateName,
      emoji = "1f4cb",
      color = "#6366f1",
      shortDescription = "",
    ) => {
      const templateId = `custom_${Date.now()}`;
      const template = {
        id: templateId,
        templateId,
        name: templateName,
        description: "Custom template",
        shortDescription,
        dayStartTime,
        dayEndTime,
        emoji,
        color,
        // Save block structure based on type:
        // - Time blocks: keep name and color
        // - Activity blocks: keep activityId, name, and color
        // - Task blocks: keep time boundaries, name, color, clear taskIds
        blocks: Object.values(blocks).map((block) => {
          const baseBlock = {
            id: block.id,
            type: block.type,
            startTime: block.startTime,
            endTime: block.endTime,
            color: block.color,
          };

          switch (block.type) {
            case "time":
              return { ...baseBlock, name: block.name };
            case "activity":
              return {
                ...baseBlock,
                name: block.name,
                activityId: block.activityId,
              };
            case "task":
              return { ...baseBlock, name: block.name || "Tasks", taskIds: [] };
            default:
              return baseBlock;
          }
        }),
      };

      // Save to local state immediately for responsiveness
      setSavedTemplates((prev) => ({ ...prev, [templateId]: template }));
      setCurrentTemplate(templateId);

      // Save to API in background
      try {
        await createTemplate(template);
      } catch (error) {
        console.error("Error saving template to API:", error);
      }

      return templateId;
    },
    [blocks, dayStartTime, dayEndTime, createTemplate],
  );

  // Update an existing template
  const updateTemplate = useCallback(
    async (templateId, updates) => {
      // Don't allow updating built-in templates
      if (templateId === "empty") return false;

      const existingTemplate = savedTemplates[templateId];
      if (!existingTemplate) return false;

      const updatedTemplate = {
        ...existingTemplate,
        ...updates,
      };

      // Update local state immediately for responsiveness
      setSavedTemplates((prev) => ({
        ...prev,
        [templateId]: updatedTemplate,
      }));

      // Update API in background
      try {
        await updateTemplateAPI(templateId, updates);
        return true;
      } catch (error) {
        console.error("Error updating template:", error);
        // Revert on error
        setSavedTemplates((prev) => ({
          ...prev,
          [templateId]: existingTemplate,
        }));
        return false;
      }
    },
    [savedTemplates, updateTemplateAPI],
  );

  // Delete a template
  const deleteTemplate = useCallback(
    async (templateId) => {
      // Don't allow deleting built-in templates
      if (templateId === "empty") return false;

      const existingTemplate = savedTemplates[templateId];
      if (!existingTemplate) return false;

      // Remove from local state immediately for responsiveness
      setSavedTemplates((prev) => {
        const newTemplates = { ...prev };
        delete newTemplates[templateId];
        return newTemplates;
      });

      // If the deleted template was current, reset to template selection
      if (currentTemplate === templateId) {
        setCurrentTemplate(null);
        setSelectedTool("templateSelect");
      }

      // Delete from API in background
      try {
        await deleteTemplateAPI(templateId);
        return true;
      } catch (error) {
        console.error("Error deleting template:", error);
        // Revert on error
        setSavedTemplates((prev) => ({
          ...prev,
          [templateId]: existingTemplate,
        }));
        return false;
      }
    },
    [savedTemplates, currentTemplate, deleteTemplateAPI],
  );

  // Clear the current day (reset to empty state and delete from DDB)
  const clearDay = useCallback(async () => {
    // Cancel any pending saves
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    pendingSaveRef.current = null;

    // Delete from DynamoDB
    try {
      await deleteDayPlan(currentDate);
    } catch (error) {
      console.error("Error deleting day plan:", error);
    }

    // Clear from cache
    delete preloadedPlansRef.current[currentDate];

    // Reset local state
    setBlocks({});
    setNextBlockId(1);
    setCurrentTemplate(null);
    setSelectedBlockId(null);
    setHasLoadedPlan(false);
    setDayStartTime("08:00");
    setDayEndTime("22:00");
    setTimeInterval(30);
    setSelectedTool("templateSelect");
  }, [currentDate, deleteDayPlan]);

  // Navigate to the next day
  const goToNextDay = useCallback(() => {
    setCurrentDate((prevDate) => addDaysToDate(prevDate, 1));
  }, []);

  // Navigate to the previous day
  const goToPreviousDay = useCallback(() => {
    setCurrentDate((prevDate) => addDaysToDate(prevDate, -1));
  }, []);

  // Select a block for editing
  const selectBlock = useCallback((blockId) => {
    setSelectedBlockId(blockId);
    setSelectedTool("editBlock");
  }, []);

  // Helper functions for mobile tool panel dialog
  const openToolPanel = useCallback((tool) => {
    if (tool) {
      setSelectedTool(tool);
    }
    setToolPanelOpen(true);
  }, []);

  const closeToolPanel = useCallback(() => {
    setToolPanelOpen(false);
  }, []);

  // Get available templates (built-in + saved)
  const availableTemplates = useMemo(
    () => ({
      ...TEMPLATES,
      ...savedTemplates,
    }),
    [savedTemplates],
  );

  const state = useMemo(
    () => ({
      // UI state
      selectedTool,
      setSelectedTool,
      selectedBlockId,
      setSelectedBlockId,
      selectBlock,
      activeTime,
      setActiveTime,
      toolPanelOpen,
      setToolPanelOpen,
      openToolPanel,
      closeToolPanel,

      // Loading and persistence state
      isLoading,
      isNavigating,
      isSaving,

      // Current date (for multi-day support)
      currentDate,
      setCurrentDate,
      goToNextDay,
      goToPreviousDay,

      // Data
      blocks,
      sortedBlocks,
      activities,
      tasks,

      // Block operations
      addBlock,
      updateBlock,
      deleteBlock,

      // Template operations
      currentTemplate,
      availableTemplates,
      loadTemplate,
      saveAsTemplate,
      updateTemplate,
      deleteTemplate,
      clearDay,

      // Day configuration
      day: {
        startTime: dayStartTime,
        setStartTime: setDayStartTime,
        endTime: dayEndTime,
        setEndTime: setDayEndTime,
        timeInterval,
        setTimeInterval,
      },

      // Utilities
      timeToMinutes,
      minutesToTime,
      snapToInterval,

      // Dev utilities (for testing time-based features)
      mockTime,
      setMockTime,
      getCurrentTime,
    }),
    [
      selectedTool,
      selectedBlockId,
      activeTime,
      toolPanelOpen,
      openToolPanel,
      closeToolPanel,
      isLoading,
      isNavigating,
      isSaving,
      currentDate,
      goToNextDay,
      goToPreviousDay,
      blocks,
      sortedBlocks,
      activities,
      tasks,
      dayStartTime,
      dayEndTime,
      timeInterval,
      currentTemplate,
      availableTemplates,
      addBlock,
      updateBlock,
      deleteBlock,
      loadTemplate,
      saveAsTemplate,
      updateTemplate,
      deleteTemplate,
      clearDay,
      selectBlock,
      mockTime,
      getCurrentTime,
    ],
  );

  return (
    <PlannerContext.Provider value={state}>{children}</PlannerContext.Provider>
  );
};

export default PlannerContextProvider;
